/**
 * Schermata di consenso del connettore MCP — parte pura.
 *
 * Il backend valida la richiesta OAuth e rimanda qui con i parametri
 * nell'indirizzo: nessuno stato lato server, quindi il flusso sopravvive a
 * riavvii, a piu' repliche e al passaggio dal login.
 */

export interface ConsentParams {
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state?: string;
  scope?: string;
}

/** Permessi noti, nell'ordine in cui ha senso mostrarli all'utente. */
const KNOWN_SCOPES = [
  'dma:read',
  'dma:write',
  'datacapture:read',
  'datacapture:write',
  'datacapture:config',
  'pm:read',
  'pm:write',
  'engagement:read',
  'engagement:write',
  'admin:read',
  'ma:write',
];

/**
 * Solo http/https sono destinazioni ammesse. Senza questo controllo un
 * `redirect_uri` come `javascript:...` finirebbe in `window.location.assign()`
 * e verrebbe ESEGUITO nell'origine della piattaforma: chi apre un link
 * preparato ad arte e clicca "Annulla" regalerebbe il proprio token.
 */
export function isSafeRedirect(uri: string): boolean {
  try {
    const { protocol } = new URL(uri);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Legge i parametri dalla query string. Restituisce null se manca qualcosa di
 * obbligatorio o se la destinazione non e' una URL http/https: in quel caso la
 * pagina non propone nessuna autorizzazione, perche' non saprebbe nemmeno dove
 * riportare l'utente in modo sicuro.
 */
export function readConsentParams(search: string): ConsentParams | null {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const client_id = q.get('client_id');
  const redirect_uri = q.get('redirect_uri');
  const code_challenge = q.get('code_challenge');
  const code_challenge_method = q.get('code_challenge_method');

  if (!client_id || !redirect_uri || !code_challenge || !code_challenge_method) return null;
  if (!isSafeRedirect(redirect_uri)) return null;

  const state = q.get('state');
  const scope = q.get('scope');

  return {
    client_id,
    redirect_uri,
    code_challenge,
    code_challenge_method,
    ...(state ? { state } : {}),
    ...(scope ? { scope } : {}),
  };
}

/**
 * Indirizzo a cui riportare il client quando l'utente annulla.
 * `access_denied` e' la risposta prevista da OAuth 2.1 per il rifiuto: il client
 * la riconosce e non resta in attesa.
 */
export function buildDenyUrl(redirectUri: string, state?: string): string | null {
  // Cintura e bretelle: i parametri sono gia' filtrati da readConsentParams, ma
  // questa funzione non deve poter produrre una destinazione eseguibile.
  if (!isSafeRedirect(redirectUri)) return null;
  const url = new URL(redirectUri);
  url.searchParams.set('error', 'access_denied');
  if (state) url.searchParams.set('state', state);
  return url.toString();
}

/** Chiave i18n con cui descrivere un permesso in parole comprensibili. */
export function scopeLabelKey(scope: string): string {
  return KNOWN_SCOPES.includes(scope) ? `consent.scopes.${scope}` : 'consent.scopes.unknown';
}
